import { TablelistProApi } from '@lib/api'
import { toSearchParams } from '@utils/object'

import { Venue, VenueDaySettings, VenueEvent } from 'types/model'
import { ListParams, ReadParams } from 'types/api/common'
import { InventoryItem } from 'types/model/inventory'
import { getDateFromNow } from '@utils/date-time'
import { VenueLayout, VenueLayoutSection, VenueLayoutTable } from 'types/model/venue-layout'
import { AppConfig } from 'types/model/appConfig'

class VenueApi extends TablelistProApi<Venue> {
  constructor() {
    super('/v1/venues')
  }

  async readAppConfig(venueId: string, readParams: ReadParams = {}) {
    const searchParams = toSearchParams(readParams)
    const response = await this.get<AppConfig>(
      `${this.basePath}/${venueId}/app-config`,
      searchParams
    )
    return response
  }

  async listEvents(
    venueId: string,
    {
      limit = 25,
      skip = 0,
      sort = 'dateStart',
      dateStart = getDateFromNow().toISOString(),
      dateEnd = getDateFromNow({ days: 30 }).toISOString(),
      ...params
    }: ListParams<VenueEvent> = {}
  ) {
    const listParams = { limit, skip, sort, dateStart, dateEnd, ...params }
    const searchParams = toSearchParams(listParams)
    const response = await this.get<{ data: VenueEvent[]; count: number }>(
      `${this.basePath}/${venueId}/events`,
      searchParams
    )
    return response
  }

  async listDaySettings(venueId: string, params: ListParams<VenueDaySettings>) {
    const response = await this.get<VenueDaySettings>(
      `${this.basePath}/${venueId}/day-settings`,
      toSearchParams(params)
    )
    return response
  }

  async listInventory(
    venueId: string,
    {
      limit = 25,
      skip = 0,
      sort,
      dateStart = getDateFromNow().toISOString(),
      dateEnd = getDateFromNow({ days: 30 }).toISOString(),
      ...params
    }: ListParams<InventoryItem> = {}
  ) {
    const listParams = { limit, skip, sort, dateStart, dateEnd, ...params }
    const searchParams = toSearchParams(listParams)
    const response = await this.get<{ data: InventoryItem[]; count: number }>(
      `${this.basePath}/${venueId}/inventory`,
      searchParams
    )
    return response
  }

  async listLayouts(venueId: string, params: ListParams<VenueLayout> = {}) {
    const response = await this.get<VenueLayout[]>(
      `/venue/${venueId}/layout`,
      toSearchParams(params)
    )
    return response
  }

  async listSections(venueId: string, params: ListParams<VenueLayoutSection> = {}) {
    const response = await this.get<VenueLayoutSection[]>(
      `/venue/${venueId}/layout-section`,
      toSearchParams(params)
    )
    return response
  }

  async listTables(venueId: string, params: ListParams<VenueLayoutTable> = {}) {
    const response = await this.get<VenueLayoutTable[]>(
      `/venue/${venueId}/layout-table`,
      toSearchParams(params)
    )
    return response
  }

  async readEvent(venueId: string, eventId: string, readParams: ReadParams = {}) {
    const searchParams = toSearchParams(readParams)
    const response = await this.get<VenueEvent>(
      `${this.basePath}/${venueId}/events/${eventId}`,
      searchParams
    )
    return response
  }

  async readDaySettings(venueId: string, dayId: string, params: ReadParams = {}) {
    const response = await this.get<VenueDaySettings>(
      `${this.basePath}/${venueId}/day-settings/${dayId}`,
      toSearchParams(params)
    )
    return response
  }

  async readInventory(venueId: string, inventoryId: string, readParams: ReadParams = {}) {
    const searchParams = toSearchParams(readParams)
    const response = await this.get<InventoryItem>(
      `${this.basePath}/${venueId}/inventory/${inventoryId}`,
      searchParams
    )
    return response
  }

  async readLayouts(venueId: string, params: ReadParams = {}) {
    const response = await this.get<VenueLayout>(
      `${this.basePath}/${venueId}/layout`,
      toSearchParams(params)
    )
    return response
  }

  async readSections(venueId: string, params: ReadParams = {}) {
    const response = await this.get<VenueLayoutSection>(
      `${this.basePath}/${venueId}/layout-section`,
      toSearchParams(params)
    )
    return response
  }

  async readTables(venueId: string, params: ReadParams = {}) {
    const response = await this.get<VenueLayoutTable>(
      `${this.basePath}/${venueId}/layout-table`,
      toSearchParams(params)
    )
    return response
  }
}

export const venueApi = new VenueApi()
